/*
 * -----------------------------------------------------------------------
 *  <copyright file="risks-and-controls-import-dialog/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { pick } from 'lodash';
import {
    ImportLibraryControls,
    ImportLibraryControlsInstructions,
} from '@components/admin/risks-and-controls-import-dialog/import-library/import-library-control/import-library-controls';
import {
    ImportLibraryRisks,
    ImportLibraryRisksInstructions,
} from '@components/admin/risks-and-controls-import-dialog/import-library/import-library-risk/import-library-risks';
import {
    LibraryControlSystemUpdate,
    LibraryControlOrgUpdate,
} from '@pages/admin/admin-risks/library-control-update.interface';
import { LibraryRiskSystemUpdate, LibraryRiskOrgUpdate } from '@pages/admin/admin-risks/library-risk-update.interface';
import { RisksAndControlsImportDialogProps } from './risks-and-controls-import-dialog.interface';
import { useBoolean, useTranslation } from 'hooks';
import { LibraryControlFieldsExtended } from 'interfaces/library-control.interface';
import { LibraryRiskFieldsExtended } from 'interfaces/library-risk.interface';
import { GET_NAMED_ITEMS } from 'queries/common';
import Constants from 'utils/constants';
import importLibraryControls from 'utils/importers/import-library-controls';
import importLibraryRisks from 'utils/importers/import-library-risks';
import Button from 'views/components/core/buttons/button';
import ImportDialog from 'views/components/core/import-dialog';
import ImportErrorsV2 from 'views/components/core/import-errors-v2';
import Loading from 'views/components/core/loading';
import { getNamedItems, getNamedItemsVariables } from '@queries/generated/common.generated';
import { NamedItemFields } from '@queries/generated/fragments.generated';
import {
    GET_LIBRARY_CONTROLS,
    GET_LIBRARY_RISKS,
    UPDATE_LIBRARY_CONTROLS,
    UPDATE_LIBRARY_RISKS,
} from '@queries/legacy-risk-assessment';

const RisksAndControlsImportDialog = ({
    isRisk,
    organisationId,
    importDialogOpen,
}: RisksAndControlsImportDialogProps) => {
    const { t } = useTranslation();

    const [importData, setImportData] = useState(null);
    const [importErrors, setImportErrors] = useState(null);

    const isImporting = useBoolean(false);

    const [saveLibraryControls] = useMutation(UPDATE_LIBRARY_CONTROLS);
    const [saveLibraryRisks] = useMutation(UPDATE_LIBRARY_RISKS);

    const { data: lovData } = useQuery<getNamedItems, getNamedItemsVariables>(GET_NAMED_ITEMS, {
        variables: { includeSystemItems: true, includeStudyItems: false },
    });

    const isSystemLevel = organisationId === Constants.ObjectId.Empty;
    const importLibrary: (
        file: any,
        lovs: Exclude<NamedItemFields, 'auditId'>[],
        isSystemLevel: boolean,
    ) => Promise<unknown> = isRisk ? importLibraryRisks : importLibraryControls;
    const libraryControl = isSystemLevel ? LibraryControlSystemUpdate : LibraryControlOrgUpdate;
    const LibraryRisk = isSystemLevel ? LibraryRiskSystemUpdate : LibraryRiskOrgUpdate;
    const libraryItemUpdate = isRisk ? LibraryRisk : libraryControl;
    const saveItemsMutation = isRisk ? saveLibraryRisks : saveLibraryControls;

    const doImport = (items: (LibraryRiskFieldsExtended | LibraryControlFieldsExtended)[]) => {
        const itemsToAdd = (items || []).map((element) => {
            const value: any = pick(element, libraryItemUpdate);
            return {
                ...value,
                id: null,
                organisationId,
                active: true,
                ...(isRisk ? { libraryControlIds: [] } : {}),
            };
        });
        return saveItemsMutation({
            variables: isRisk
                ? {
                      risks: itemsToAdd,
                  }
                : {
                      controls: itemsToAdd,
                  },
            refetchQueries: [
                {
                    query: GET_LIBRARY_RISKS,
                    variables: {
                        organisationId,
                        includeSystemRisks: isSystemLevel,
                    },
                },
                {
                    query: GET_LIBRARY_CONTROLS,
                    variables: {
                        organisationId,
                        includeSystemControls: isSystemLevel,
                    },
                },
            ],
        });
    };

    const clearDataAndErrors = (closeDialog?: boolean) => {
        setImportErrors(null);
        setImportData(null);
        if (closeDialog) {
            importDialogOpen.setFalse();
        }
    };

    const onDialogClose = () => clearDataAndErrors(true);

    const onSaveClick = () => {
        isImporting.setTrue();
        if (importData?.length) {
            doImport(importData).then(() => {
                isImporting.setFalse();
                clearDataAndErrors(true);
            });
        }
    };

    const onDataSelect = (files: any) => {
        clearDataAndErrors();

        if (files?.length) {
            isImporting.setTrue();
            importLibrary(files[0], lovData?.namedItems?.filter((ni) => ni != null) || [], isSystemLevel)
                .then(({ data: importedData, errors }: any) => {
                    isImporting.setFalse();

                    if (errors?.length) setImportErrors(errors);
                    else if (importedData?.length) setImportData(importedData);
                    else setImportErrors([{ line: null, message: 'No risks found to import!' }]);
                })
                .catch((ex: any) => {
                    console.log('Import exception: ', ex);
                    isImporting.setFalse();
                    setImportErrors([{ line: null, message: ex.length > 0 ? ex[0].message : ex.message }]);
                });
        }
    };

    const submitBtn = (
        <Button
            id="import-items-btn"
            className="add-cv-btn add-risk-btn main-button"
            disabled={isImporting.value}
            onClick={onSaveClick}
        >
            {t('common:save')}
        </Button>
    );

    const importingTemplate = (
        <>
            <div className="secondary" style={{ fontSize: 18, textAlign: 'center', margin: 'auto', marginBottom: 20 }}>
                {t('ra:importing')}
            </div>
            <Loading float={false} />
        </>
    );

    const errorsTemplate = importErrors?.length ? (
        <ImportErrorsV2 errors={importErrors} />
    ) : importData?.length ? (
        isRisk ? (
            <ImportLibraryRisks data={importData} lovs={lovData.namedItems} isSystemLevel={isSystemLevel} />
        ) : (
            <ImportLibraryControls data={importData} lovs={lovData.namedItems} isSystemLevel={isSystemLevel} />
        )
    ) : isRisk ? (
        <ImportLibraryRisksInstructions isSystemLevel={isSystemLevel} />
    ) : (
        <ImportLibraryControlsInstructions isSystemLevel={isSystemLevel} />
    );

    return (
        <ImportDialog
            title={t(isRisk ? 'ra:importRisks' : 'ra:importControls')}
            open
            disabled={isImporting.value}
            button={!!importData?.length && submitBtn}
            template={isImporting.value ? importingTemplate : errorsTemplate}
            onSelect={onDataSelect}
            onClose={onDialogClose}
        />
    );
};

export default RisksAndControlsImportDialog;
